import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { MdOutlineMail, MdPhone, MdSend } from "react-icons/md";
import { HiSparkles } from "react-icons/hi";
import "./Contactme.css";
import axios from "axios";

const Contactme = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validation
    if (
      !formData.name ||
      !formData.email ||
      !formData.message ||
      !formData.subject
    ) {
      setStatus({
        type: "error",
        message: "Please fill in all required fields.",
      });
      return;
    }

    // email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    // set loading state
    setStatus({
      type: "loading",
      message: "Sending message...",
    });

    try {
      const API_URL = process.env.REACT_APP_API_URL;

      const res = await axios.post(
        `${API_URL}/api/v1/contact-form`,
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 3000,
        },
      );

      if (res.data.success) {
        setStatus({
          type: "success",
          message: "Message sent successfully!",
        });

        // reset form
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });

        // clear success message after 3 seconds
        setTimeout(() => {
          setStatus({ type: "", message: "" });
        }, 3000);
      }
    } catch (error) {
      setStatus({
        type: "error",
        message:
          "Failed to send message. Please try again or email me directly.",
      });

      // clear error message after 3 seconds
      setTimeout(() => {
        setStatus({ type: "", message: "" });
      }, 3000);
    }
  };

  const contactInfo = [
    {
      icon: <MdOutlineMail />,
      title: "Email",
      value: "willyokto123@gmail.com",
      link: "mailto:willyokto123@gmail.com",
      color: "#667eea",
    },
    {
      icon: <MdPhone />,
      title: "Phone",
      value: "+6285857321597",
      link: "tel:+6285857321597",
      color: "#f5576c",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section className="contact-section" id="contact" ref={ref}>
      <div className="container">
        {/* Header */}
        <motion.div
          className="contact-header"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="section-badge"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <HiSparkles className="badge-icon" />
            Contact Me
          </motion.div>
          <h2 className="section-title">
            Let's Work <span className="gradient-text">Together</span>
          </h2>
        </motion.div>

        <div className="contact-content">
          {/* Contact Info Cards */}
          <motion.div
            className="contact-info-container"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.link}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-info-card glass-card"
                variants={itemVariants}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="contact-icon"
                  style={{ color: info.color }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {info.icon}
                </motion.div>
                <div className="contact-text">
                  <h4>{info.title}</h4>
                  <p>{info.value}</p>
                </div>
              </motion.a>
            ))}
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="contact-form-container"
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="contact-form-wrapper glass-card">
              <h3 className="form-title">Send Me a Message</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  {/* Name */}
                  <motion.div
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 }}
                  >
                    <label htmlFor="name">
                      Name <span className="span-required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      required
                      disabled={status.type === "loading"}
                    />
                  </motion.div>

                  {/* Email */}
                  <motion.div
                    className="form-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5 }}
                  >
                    <label htmlFor="email">
                      Email <span className="span-required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email ex.your@gmail.com"
                      required
                      disabled={status.type === "loading"}
                    />
                  </motion.div>
                </div>

                {/* Subject */}
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 }}
                >
                  <label htmlFor="subject">
                    Subject <span className="span-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this about?"
                    required
                    disabled={status.type === "loading"}
                  />
                </motion.div>

                {/* Message */}
                <motion.div
                  className="form-group"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.7 }}
                >
                  <label htmlFor="message">
                    Message <span className="span-required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Send me message..."
                    rows="8"
                    required
                    disabled={status.type === "loading"}
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  className={`btn-modern btn-primary submit-btn ${
                    status.type === "loading" ? "loading" : ""
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 }}
                  whileHover={status.type !== "loading" ? { scale: 1.02 } : {}}
                  whileTap={status.type !== "loading" ? { scale: 0.98 } : {}}
                  disabled={status.type === "loading"}
                >
                  {status.type === "loading" ? (
                    <>
                      <span className="spinner"></span>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span className="btn-logo-submit">
                        <MdSend />
                      </span>
                      <span>Send Message</span>
                    </>
                  )}
                </motion.button>

                {/* Status Message */}
                {status && (
                  <motion.div
                    className={`form-status ${status.type}`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {status.type === "success" && "✓"}
                    {status.type === "error" && "✕"}
                    {status.message}
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contactme;
